import { api } from "./client";

/** Numbers supplied by DoneTalk (decision 0018): lines rented from Salvy by DDD. */
export type RentedStatus =
  | "awaiting_code"
  | "active"
  | "cancel_scheduled"
  | "closed"
  | "transfer_requested"
  | "transferred";

export type AttachStep =
  | "adding"
  | "requesting"
  | "awaiting_sms"
  | "verifying"
  | "registering"
  | "done"
  | "failed";

export interface RentedNumber {
  id: string;
  e164: string;
  areaCode: number;
  status: RentedStatus;
  sandbox: boolean;
  channelId: string | null;
  priceBrl: number;
  createdAt: string;
  activatedAt: string | null;
  cancelAt: string | null;
  closedAt: string | null;
  returnsAt: string | null;
  transferRequestedAt: string | null;
  transferNote: string | null;
  transferFeeBrl: number | null;
  attach: {
    step: AttachStep;
    failedStep?: AttachStep;
    phoneNumberId?: string;
    displayName: string;
    error?: { message: string; code?: number };
    updatedAt: string;
  } | null;
  requestedBy: string | null;
}

export interface RentalOffer {
  available: boolean;
  sandbox: boolean;
  autoAttach: boolean;
  priceBrl: number;
  transferFeeBrl: number;
  canRent: boolean;
}

export interface RentedSms {
  id: string;
  receivedAt: string;
  origin: string | null;
  body: string;
  code: string | null;
  service: string | null;
}

export interface PlatformRentedNumber extends RentedNumber {
  customer: { id: string; name: string; slug: string };
  costBrl: number;
  quarantineUntil: string | null;
  inQuarantine: boolean;
  closedReason: string | null;
  providerActivatedAt: string | null;
  transferredAt: string | null;
}

const base = (slug: string) => `/v1/c/${slug}/numbers/rental`;

export const numbersApi = {
  rental: (slug: string) =>
    api<{ offer: RentalOffer; numbers: RentedNumber[]; monthlyBrl: number }>(
      base(slug),
    ),
  areaCodes: (slug: string) =>
    api<{ areaCodes: number[] }>(`${base(slug)}/area-codes`),
  rent: (slug: string, areaCode: number) =>
    api<{ number: RentedNumber }>(base(slug), {
      method: "POST",
      body: { areaCode, accept: true },
    }),
  sms: (slug: string, id: string, refresh = false) =>
    api<{ messages: RentedSms[] }>(`${base(slug)}/${id}/sms`, {
      query: refresh ? { refresh: 1 } : {},
    }),
  cancel: (slug: string, id: string) =>
    api<{ number: RentedNumber }>(`${base(slug)}/${id}/cancel`, {
      method: "POST",
      body: {},
    }),
  undoCancel: (slug: string, id: string) =>
    api<{ number: RentedNumber }>(`${base(slug)}/${id}/undo-cancel`, {
      method: "POST",
      body: {},
    }),
  transfer: (slug: string, id: string, note: string | null) =>
    api<{ number: RentedNumber }>(`${base(slug)}/${id}/transfer`, {
      method: "POST",
      body: { note },
    }),
  simulateSms: (slug: string, id: string) =>
    api<{ messages: RentedSms[] }>(`${base(slug)}/${id}/simulate-sms`, {
      method: "POST",
      body: {},
    }),
  attach: (
    slug: string,
    id: string,
    body: { code: string; wabaId?: string; displayName: string },
  ) =>
    api<{ number: RentedNumber }>(`${base(slug)}/${id}/attach`, {
      method: "POST",
      body,
    }),
  retryAttach: (slug: string, id: string) =>
    api<{ number: RentedNumber }>(`${base(slug)}/${id}/attach/retry`, {
      method: "POST",
      body: {},
    }),
  // Nuvvun console
  platformList: () =>
    api<{
      numbers: PlatformRentedNumber[];
      totals: {
        active: number;
        chargedBrl: number;
        paidBrl: number;
        transferFeesBrl: number;
        priceBrl: number;
        costBrl: number;
      };
    }>("/v1/platform/numbers"),
  platformTransferred: (id: string) =>
    api<{ number: RentedNumber }>(`/v1/platform/numbers/${id}/transferred`, {
      method: "POST",
      body: {},
    }),
  platformCancel: (id: string, when: "now" | "end_of_cycle") =>
    api<{ number: RentedNumber }>(`/v1/platform/numbers/${id}/cancel`, {
      method: "POST",
      body: { when },
    }),
};

/** +5541963475701 → +55 41 96347-5701. */
export function formatPhone(e164: string): string {
  const d = e164.replace(/\D/g, "");
  if (d.startsWith("55") && (d.length === 12 || d.length === 13)) {
    const local = d.slice(4);
    const split = local.length - 4;
    return `+55 ${d.slice(2, 4)} ${local.slice(0, split)}-${local.slice(split)}`;
  }
  return e164;
}

/** 690498 → 690 498, the way the screen shows a WhatsApp code. */
export function formatCode(code: string): string {
  return code.length === 6 ? `${code.slice(0, 3)} ${code.slice(3)}` : code;
}

/** DDDs by state, capitals first inside each state (the rent dialog groups them). */
export const AREA_CODES: Record<
  number,
  { city: string; uf: string; capital?: boolean }
> = {
  11: { city: "São Paulo", uf: "SP", capital: true },
  12: { city: "São José dos Campos", uf: "SP" },
  13: { city: "Santos", uf: "SP" },
  14: { city: "Bauru", uf: "SP" },
  15: { city: "Sorocaba", uf: "SP" },
  16: { city: "Ribeirão Preto", uf: "SP" },
  17: { city: "São José do Rio Preto", uf: "SP" },
  18: { city: "Presidente Prudente", uf: "SP" },
  19: { city: "Campinas", uf: "SP" },
  21: { city: "Rio de Janeiro", uf: "RJ", capital: true },
  22: { city: "Campos dos Goytacazes", uf: "RJ" },
  24: { city: "Petrópolis", uf: "RJ" },
  27: { city: "Vitória", uf: "ES", capital: true },
  28: { city: "Cachoeiro de Itapemirim", uf: "ES" },
  31: { city: "Belo Horizonte", uf: "MG", capital: true },
  32: { city: "Juiz de Fora", uf: "MG" },
  33: { city: "Governador Valadares", uf: "MG" },
  34: { city: "Uberlândia", uf: "MG" },
  35: { city: "Poços de Caldas", uf: "MG" },
  37: { city: "Divinópolis", uf: "MG" },
  38: { city: "Montes Claros", uf: "MG" },
  41: { city: "Curitiba", uf: "PR", capital: true },
  42: { city: "Ponta Grossa", uf: "PR" },
  43: { city: "Londrina", uf: "PR" },
  44: { city: "Maringá", uf: "PR" },
  45: { city: "Foz do Iguaçu", uf: "PR" },
  46: { city: "Francisco Beltrão", uf: "PR" },
  47: { city: "Joinville", uf: "SC" },
  48: { city: "Florianópolis", uf: "SC", capital: true },
  49: { city: "Chapecó", uf: "SC" },
  51: { city: "Porto Alegre", uf: "RS", capital: true },
  53: { city: "Pelotas", uf: "RS" },
  54: { city: "Caxias do Sul", uf: "RS" },
  55: { city: "Santa Maria", uf: "RS" },
  61: { city: "Brasília", uf: "DF", capital: true },
  62: { city: "Goiânia", uf: "GO", capital: true },
  63: { city: "Palmas", uf: "TO", capital: true },
  64: { city: "Rio Verde", uf: "GO" },
  65: { city: "Cuiabá", uf: "MT", capital: true },
  66: { city: "Rondonópolis", uf: "MT" },
  67: { city: "Campo Grande", uf: "MS", capital: true },
  68: { city: "Rio Branco", uf: "AC", capital: true },
  69: { city: "Porto Velho", uf: "RO", capital: true },
  71: { city: "Salvador", uf: "BA", capital: true },
  73: { city: "Ilhéus", uf: "BA" },
  74: { city: "Juazeiro", uf: "BA" },
  75: { city: "Feira de Santana", uf: "BA" },
  77: { city: "Vitória da Conquista", uf: "BA" },
  79: { city: "Aracaju", uf: "SE", capital: true },
  81: { city: "Recife", uf: "PE", capital: true },
  82: { city: "Maceió", uf: "AL", capital: true },
  83: { city: "João Pessoa", uf: "PB", capital: true },
  84: { city: "Natal", uf: "RN", capital: true },
  85: { city: "Fortaleza", uf: "CE", capital: true },
  86: { city: "Teresina", uf: "PI", capital: true },
  87: { city: "Petrolina", uf: "PE" },
  88: { city: "Juazeiro do Norte", uf: "CE" },
  89: { city: "Picos", uf: "PI" },
  91: { city: "Belém", uf: "PA", capital: true },
  92: { city: "Manaus", uf: "AM", capital: true },
  93: { city: "Santarém", uf: "PA" },
  94: { city: "Marabá", uf: "PA" },
  95: { city: "Boa Vista", uf: "RR", capital: true },
  96: { city: "Macapá", uf: "AP", capital: true },
  97: { city: "Coari", uf: "AM" },
  98: { city: "São Luís", uf: "MA", capital: true },
  99: { city: "Imperatriz", uf: "MA" },
};
